import React, { useEffect, useState, useCallback } from 'react';
import { discussionAPI, curriculumAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CoachLayout from '../../pages/coach/CoachLayout';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function ModerationQueue() {
  const { authoringDomains, isAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [domainFilter, setDomainFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [domainCatalog, setDomainCatalog] = useState([]);

  useEffect(() => {
    curriculumAPI.listDomains()
      .then(({ data }) => setDomainCatalog(Array.isArray(data?.domains) ? data.domains : []))
      .catch(() => setDomainCatalog([]));
  }, []);

  const domains = React.useMemo(() => {
    if (domainCatalog.length) {
      const catalog = isAdmin
        ? domainCatalog
        : domainCatalog.filter((d) => (authoringDomains || []).includes(d.domain_id));
      return catalog.map((d) => ({ id: d.domain_id, label: d.display_name || d.domain_id }));
    }
    return (authoringDomains || []).map((id) => ({ id, label: id }));
  }, [domainCatalog, authoringDomains, isAdmin]);

  const fetchQueue = useCallback(() => {
    setLoading(true);
    discussionAPI
      .getModerationQueue(domainFilter || undefined, statusFilter)
      .then((r) => {
        setPosts(r.data?.posts || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [domainFilter, statusFilter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const ACTION_TO_VISIBILITY = { approve: 'visible', hide: 'hidden', reject: 'rejected' };

  const handleAction = async (postId, action, reason = null) => {
    setActionLoading(postId);
    try {
      const visibility = ACTION_TO_VISIBILITY[action] || action;
      await discussionAPI.moderatePost(postId, visibility, reason);
      fetchQueue();
    } catch { /* best-effort */ }
    setActionLoading(null);
    setRejectModal(null);
    setRejectReason('');
  };

  return (
    <CoachLayout title="Moderation">
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Discussion Moderation</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#64748b' }}>
            Review and moderate learner discussion posts
          </p>
        </div>
        <div style={badgeStyle}>{total} pending</div>
      </div>

      <div style={filtersStyle}>
        <div style={filterGroupStyle}>
          <label style={labelStyle}>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <div style={filterGroupStyle}>
          <label style={labelStyle}>Domain</label>
          <select
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">All domains</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div style={mutedStyle}>Loading queue…</div>}

      {!loading && posts.length === 0 && (
        <div style={emptyStyle}>
          <div style={{ fontSize: 40, marginBottom: 8, opacity: 0.5 }}>✅</div>
          No posts to moderate. All clear!
        </div>
      )}

      {!loading && posts.length > 0 && (
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Post</th>
                <th style={thStyle}>Author</th>
                <th style={thStyle}>Domain</th>
                <th style={thStyle}>Scene</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.post_id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={bodyPreviewStyle}>
                      {post.body_md?.slice(0, 120)}
                      {(post.body_md?.length || 0) > 120 ? '…' : ''}
                    </div>
                    {post.moderation_reason && (
                      <div style={reasonStyle}>
                        {post.moderation_reason}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 500 }}>{post.display_name}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={domainChipStyle}>{post.domain}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{post.scene_id}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={statusChipStyle(post.visibility)}>
                      {post.visibility}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      {timeAgo(post.created_at)}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleAction(post.post_id, 'approve')}
                        disabled={actionLoading === post.post_id}
                        style={approveBtnStyle}
                        title="Approve"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setRejectModal(post.post_id);
                          setRejectReason('');
                        }}
                        disabled={actionLoading === post.post_id}
                        style={rejectBtnStyle}
                        title="Reject"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleAction(post.post_id, 'hide')}
                        disabled={actionLoading === post.post_id}
                        style={hideBtnStyle}
                        title="Hide"
                      >
                        Hide
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejectModal && (
        <>
          <div onClick={() => setRejectModal(null)} style={overlayStyle} />
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Reject Post</h3>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
              Provide a reason for rejection (visible to the learner).
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection…"
              rows={3}
              style={textareaStyle}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button
                onClick={() => setRejectModal(null)}
                style={cancelBtnStyle}
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction(rejectModal, 'reject', rejectReason)}
                style={confirmRejectBtnStyle}
              >
                Reject Post
              </button>
            </div>
          </div>
        </>
      )}
    </div>
    </CoachLayout>
  );
}

const pageStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
};
const badgeStyle = {
  background: '#fef3c7',
  color: '#92400e',
  padding: '4px 12px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 700,
};
const filtersStyle = {
  display: 'flex',
  gap: 16,
  marginBottom: 16,
};
const filterGroupStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#475569' };
const selectStyle = {
  padding: '5px 10px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 13,
  color: '#1e293b',
  background: '#fff',
};
const mutedStyle = { color: '#94a3b8', fontSize: 14, padding: '24px 0' };
const emptyStyle = {
  textAlign: 'center',
  padding: '48px 0',
  color: '#94a3b8',
  fontSize: 15,
};
const tableContainerStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  overflow: 'hidden',
};
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};
const thStyle = {
  textAlign: 'left',
  padding: '10px 14px',
  fontSize: 12,
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  background: '#f8fafc',
  borderBottom: '1px solid #e5e7eb',
};
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '12px 14px', verticalAlign: 'top' };
const bodyPreviewStyle = {
  fontSize: 13,
  lineHeight: 1.5,
  color: '#1f2937',
  maxWidth: 320,
};
const reasonStyle = {
  marginTop: 4,
  fontSize: 11,
  color: '#dc2626',
  fontStyle: 'italic',
};
const domainChipStyle = {
  fontSize: 11,
  fontWeight: 600,
  background: '#e0e7ff',
  color: '#3730a3',
  padding: '2px 8px',
  borderRadius: 999,
};
const statusChipStyle = (status) => {
  const colors = {
    pending: { bg: '#fef3c7', fg: '#92400e' },
    rejected: { bg: '#fee2e2', fg: '#991b1b' },
    hidden: { bg: '#f1f5f9', fg: '#64748b' },
    visible: { bg: '#dcfce7', fg: '#166534' },
  };
  const c = colors[status] || colors.pending;
  return {
    fontSize: 11,
    fontWeight: 600,
    background: c.bg,
    color: c.fg,
    padding: '2px 8px',
    borderRadius: 999,
  };
};
const approveBtnStyle = {
  background: '#059669',
  color: '#fff',
  border: 'none',
  borderRadius: 5,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const rejectBtnStyle = {
  background: '#dc2626',
  color: '#fff',
  border: 'none',
  borderRadius: 5,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const hideBtnStyle = {
  background: '#64748b',
  color: '#fff',
  border: 'none',
  borderRadius: 5,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.3)',
  zIndex: 9000,
};
const modalStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  width: 420,
  maxWidth: '90vw',
  zIndex: 9001,
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};
const textareaStyle = {
  width: '100%',
  padding: 10,
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  lineHeight: 1.5,
  resize: 'vertical',
  boxSizing: 'border-box',
};
const cancelBtnStyle = {
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 13,
  color: '#64748b',
  cursor: 'pointer',
};
const confirmRejectBtnStyle = {
  background: '#dc2626',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
